/**
 * Calculates age from birth date
 * @param {string|Date} birthDate - Birth date in ISO format or Date object
 * @returns {number|null} - Age in years, or null if invalid date
 */
export const calculateAge = (birthDate) => {
  if (!birthDate) return null;

  try {
    const birth = new Date(birthDate);
    const today = new Date();
    
    // Check if date is valid
    if (isNaN(birth?.getTime())) return null;
    
    let age = today?.getFullYear() - birth?.getFullYear();
    const monthDiff = today?.getMonth() - birth?.getMonth();
    
    // Adjust age if birthday hasn't occurred this year yet
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birth?.getDate())) {
      age--;
    }
    
    return age >= 0 ? age : null;
  } catch (error) {
    console.error('Error calculating age:', error);
    return null;
  }
};

/**
 * Formats date to Brazilian format (DD/MM/YYYY)
 * @param {string|Date} date - Date in ISO format or Date object
 * @returns {string} - Formatted date string
 */
export const formatDateBR = (date) => {
  if (!date) return '';
  
  try {
    const d = new Date(date);
    if (isNaN(d?.getTime())) return '';
    
    return d?.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    return '';
  }
};

/**
 * Checks if passport is expired
 * @param {string|Date} validityDate - Passport validity date
 * @returns {boolean} - True if expired, false otherwise
 */
export const isPassportExpired = (validityDate) => {
  if (!validityDate) return false;
  
  try {
    const validity = new Date(validityDate);
    const today = new Date();
    
    // Set time to start of day for accurate comparison
    validity?.setHours(0, 0, 0, 0);
    today?.setHours(0, 0, 0, 0);
    
    return validity < today;
  } catch (error) {
    return false;
  }
};

/**
 * Gets days until passport expiration
 * @param {string|Date} validityDate - Passport validity date
 * @returns {number|null} - Days until expiration (negative if expired), or null if invalid
 */
export const getDaysUntilExpiration = (validityDate) => {
  if (!validityDate) return null;
  
  try {
    const validity = new Date(validityDate);
    const today = new Date();
    
    if (isNaN(validity?.getTime())) return null;
    
    // Calculate difference in days
    const diffTime = validity?.getTime() - today?.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  } catch (error) {
    return null;
  }
};

/**
 * Formats CPF with mask (###.###.###-##)
 * @param {string} cpf - CPF number
 * @returns {string} - Formatted CPF
 */
export const formatCPF = (cpf) => {
  if (!cpf) return '';
  
  // Remove non-numeric characters
  const numbers = cpf?.replace(/\D/g, '');
  
  // Apply CPF mask
  if (numbers?.length <= 11) {
    return numbers
      ?.replace(/(\d{3})(\d)/, '$1.$2')
      ?.replace(/(\d{3})(\d)/, '$1.$2')
      ?.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  
  return cpf;
};

/**
 * Validates CPF format (basic check)
 * @param {string} cpf - CPF number
 * @returns {boolean} - True if valid format, false otherwise
 */
export const isValidCPFFormat = (cpf) => {
  if (!cpf) return false;
  
  // Remove non-numeric characters
  const numbers = cpf?.replace(/\D/g, '');
  
  // Check if has 11 digits and is not all same digit
  if (numbers?.length !== 11) return false;
  if (/^(\d)\1{10}$/?.test(numbers)) return false;
  
  return true;
};